import {NamedRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/NamedRange';
import {SpreadsheetsState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SpreadsheetsState';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {PageId} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {EventProp} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {IProperty, Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {BindingId, BoxId, NamedRangeId, PropId, SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export enum RepeaterFixation
{
    NotFixed = 'notFixed',
    FixedDown = 'fixedDown',
    FixedRight = 'fixedRight',
    FixedBoth = 'fixedBoth'
}

export enum BindingDirection
{
    Right = 'rightDirection',
    Down = 'downDirection'
}

export interface IBindingSnapshot
{
    id:BindingId;
    sheetId:SheetId,
    pageId:PageId;
    boxId:BoxId;
    propId:PropId;
    rangeId:NamedRangeId;
    repeaterFixation:RepeaterFixation;
    isPropBinding:boolean;
}

export interface IPropBindingSnapshot extends IBindingSnapshot
{
    propPath:string;
    direction:BindingDirection;
    useFormat:boolean;
}

export interface IEventBindingSnapshot extends IBindingSnapshot
{
}

export interface IBinding
{
    id:BindingId;
    box:Box;
    prop:IProperty;
    range:NamedRange;
    repeaterFixation:RepeaterFixation;
    isPropBinding:boolean;

    copyForClonedBox(newBox:Box, newProp:IProperty, newRange:NamedRange):IBinding;
}

export class PropBinding implements IBinding
{
    public id:BindingId;

    public box:Box;

    public prop:Prop<any>;

    // for now composite object binding will only use consecutive columns in a range
    // if we will want to move different fields of a composite to different ranges
    // we will need to introduce fieldName => rangeId map here instead
    public range:NamedRange;

    @observable
    public repeaterFixation:RepeaterFixation;

    public isPropBinding:boolean = true;

    @observable
    public propPath:PropPath;

    @observable
    public direction:BindingDirection;

    @observable
    public useFormat:boolean;

    public static create(box:Box,
                         prop:Prop<any>,
                         propPath:PropPath,
                         range:NamedRange,
                         repeaterFixation:RepeaterFixation,
                         direction:BindingDirection,
                         useFormat:boolean,
                         id?:BindingId):PropBinding
    {
        const binding = new PropBinding();

        binding.id = id || IdUtil.newGUID();
        binding.box = box;
        binding.prop = prop;
        binding.propPath = propPath;
        binding.range = range;
        binding.repeaterFixation = repeaterFixation;
        binding.direction = direction;
        binding.useFormat = useFormat;

        return binding;
    }

    @computed
    public get isVertical()
    {
        return this.direction === BindingDirection.Down;
    }

    public copyForClonedBox(newBox:Box, newProp:Prop<any>, newRange:NamedRange):PropBinding
    {
        return PropBinding.create(
            newBox,
            newProp,
            this.propPath,
            newRange,
            this.repeaterFixation,
            this.direction,
            this.useFormat
        );
    }

    public static toJson:ITransformer<PropBinding, IPropBindingSnapshot> = createTransformer((binding:PropBinding) =>
    {
        const {id, box, prop, range, propPath, repeaterFixation, direction, useFormat, isPropBinding} = binding;

        return {
            id,
            sheetId: range.sheetId,
            pageId: box ? box.page.id : null,
            boxId: box ? box.id : null,
            propId: prop.id,
            rangeId: range.id,
            repeaterFixation,
            isPropBinding,
            propPath: propPath.asKey(),
            direction,
            useFormat
        } as IPropBindingSnapshot;
    });

    public static fromJson(json:IPropBindingSnapshot,
                           spreadsheets:SpreadsheetsState,
                           surface:SurfaceState,
                           currentState?:PropBinding,
                           currentJson?:IPropBindingSnapshot):PropBinding | null
    {
        if( !currentState )
            return PropBinding.fromJsonDeep(json, spreadsheets, surface);

        if( !currentJson )
            currentJson = PropBinding.toJson(currentState);

        const {id, sheetId, pageId, boxId, propId, rangeId, repeaterFixation, propPath, direction, useFormat} = json;

        let box = null;
        let prop;

        if( surface.props.has(propId) )
            prop = surface.props.get(propId);
        else
        {
            const page = surface.pages.get(pageId);
            box = page.artboard.allBoxes.get(boxId);
            prop = box.componentDesc.getPropById(propId);
        }

        const workbook = spreadsheets.sheetToWorkbookMap.get(sheetId);
        const range = workbook.runtime.getNameRangeById(rangeId);

        const binding = new PropBinding();

        binding.id = id;
        binding.box = box;
        binding.prop = prop;
        binding.range = range;
        binding.repeaterFixation = repeaterFixation;

        binding.propPath = propPath === currentJson.propPath
            ? currentState.propPath
            : PropPath.fromString(propPath);

        binding.direction = direction;
        binding.useFormat = useFormat;

        return binding;
    }

    public static fromJsonDeep(json:IPropBindingSnapshot,
                               spreadsheets:SpreadsheetsState,
                               surface:SurfaceState):PropBinding | null
    {
        const {id, sheetId, pageId, boxId, propId, rangeId, repeaterFixation, propPath, direction, useFormat} = json;

        let box = null;
        let prop;

        if( surface.props.has(propId) )
            prop = surface.props.get(propId);
        else
        {
            const page = surface.pages.get(pageId);

            if( !page )
            {
                console.error('ERROR!!!: Page not found. PageId:', pageId);
                return null;
            }

            box = page.artboard.allBoxes.get(boxId);

            if( !box )
            {
                console.error('ERROR!!!: Box not found. BoxId:', boxId);
                return null;
            }

            prop = box.componentDesc.getPropById(propId);

            if( !prop )
            {
                console.error('ERROR!!!: Prop not found. PropId:', propId);
                return null;
            }
        }

        const workbook = spreadsheets.sheetToWorkbookMap.get(sheetId);

        if( !workbook )
        {
            console.error('ERROR!!!: Workbook not found. SheetId:', sheetId);
            return null;
        }

        const range = workbook.runtime.getNameRangeById(rangeId);

        if( !range )
        {
            console.error('ERROR!!!: Range not found. RangeId:', rangeId);
            return null;
        }

        const binding = new PropBinding();

        binding.id = id;
        binding.box = box;
        binding.prop = prop;
        binding.range = range;
        binding.repeaterFixation = repeaterFixation;
        binding.propPath = PropPath.fromString(propPath);
        binding.direction = direction;
        binding.useFormat = useFormat;

        return binding;
    }
}

export class EventBinding implements IBinding
{
    public id:BindingId;

    public box:Box;

    public prop:EventProp;

    public range:NamedRange;

    @observable
    public repeaterFixation:RepeaterFixation;

    public isPropBinding:boolean = false;

    public static create(box:Box, eventProp:EventProp, range:NamedRange, repeaterFixation:RepeaterFixation, id?:BindingId):EventBinding
    {
        const binding = new EventBinding();

        binding.id = id || IdUtil.newGUID();
        binding.box = box;
        binding.prop = eventProp;
        binding.range = range;
        binding.repeaterFixation = repeaterFixation;

        return binding;
    }

    // use `prop` for polymorphism, but also have this for convinience and type-safety, when it's appropriate
    public get eventProp()
    {
        return this.prop;
    }

    public copyForClonedBox(newBox:Box, eventProp:EventProp, newRange:NamedRange):IBinding
    {
        return EventBinding.create(
            newBox,
            eventProp,
            newRange,
            this.repeaterFixation
        );
    }

    public static toJson:ITransformer<EventBinding, IEventBindingSnapshot> = createTransformer((binding:EventBinding) =>
    {
        const {id, box, eventProp, range, repeaterFixation, isPropBinding} = binding;
        return {
            id,
            sheetId: range.sheetId,
            pageId: box && box.page.id,
            boxId: box && box.id,
            propId: eventProp.id,
            rangeId: range.id,
            repeaterFixation,
            isPropBinding
        } as IEventBindingSnapshot;
    });

    public static fromJson(json:IEventBindingSnapshot,
                           spreadsheets:SpreadsheetsState,
                           surface:SurfaceState,
                           currentState?:EventBinding,
                           currentJson?:IEventBindingSnapshot):EventBinding | null
    {
        // no optimization here for now... just allways do full deserialization
        return EventBinding.fromJsonDeep(json, spreadsheets, surface);
    }

    public static fromJsonDeep(json:IEventBindingSnapshot,
                               spreadsheets:SpreadsheetsState,
                               surface:SurfaceState):EventBinding | null
    {
        const {id, sheetId, pageId, boxId, propId, rangeId, repeaterFixation, isPropBinding} = json;

        const binding = new EventBinding();

        let box = null;
        let eventProp;

        if( surface.eventProps.has(propId) )
            eventProp = surface.eventProps.get(propId);
        else
        {
            const page = surface.pages.get(pageId);

            if( !page )
            {
                console.error('ERROR!!!: Page not found. PageId:', pageId);
                return null;
            }

            box = page.artboard.allBoxes.get(boxId);

            if( !box )
            {
                console.error('ERROR!!!: Box not found. BoxId:', boxId);
                return null;
            }

            eventProp = box.componentDesc.getEventPropById(propId);

            if( !eventProp )
            {
                console.error('ERROR!!!: EventProp not found. EventPropId:', propId);
                return null;
            }
        }

        const workbook = spreadsheets.sheetToWorkbookMap.get(sheetId);

        if( !workbook )
        {
            console.error('ERROR!!!: Workbook not found. SheetId:', sheetId);
            return null;
        }

        const range = workbook.runtime.getNameRangeById(rangeId);

        if( !range )
        {
            console.error('ERROR!!!: Range not found. RangeId:', rangeId);
            return null;
        }

        binding.id = id;
        binding.box = box;
        binding.prop = eventProp;
        binding.range = range;
        binding.repeaterFixation = repeaterFixation;
        binding.isPropBinding = isPropBinding;

        return binding;
    }
}

export type PropBindingProcessor<T> = (pBinding:PropBinding) => T;
export type EventBindingProcessor<T> = (eBinding:EventBinding) => T;

//convenience function so we can do instanceof with type-safety afterwards
export function doWithBinding<T>(binding:IBinding,
                                 propProcessor:PropBindingProcessor<T>,
                                 eventProcessor:EventBindingProcessor<T>):T
{
    if( binding instanceof PropBinding )
        return propProcessor(binding as PropBinding);
    else if( binding instanceof EventBinding )
        return eventProcessor(binding as EventBinding);
    else
        throw new Error('Unknown binding type');
}
